import type { AccountKind } from "@/api/types";

/**
 * Display-only formatting helpers. Nothing here sums, subtracts, or
 * otherwise computes with money — every function takes a value already
 * decided by the server and turns it into a string. If a future screen
 * needs a total, that total must come from the API, not from reducing a
 * list of `Entry.amount` client-side.
 */

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const moneyFormatterCents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Whole-dollar display for KPI tiles ("$248,910") — matches the design spec. */
export function formatMoneyWhole(amount: number): string {
  return moneyFormatter.format(amount);
}

/**
 * Compact display for large balance-sheet figures ("$1.84M", "$412k") —
 * matches the design spec's Net worth panel. Only used for round fixture
 * numbers on the Reports screen; never a value a farmer needs to reconcile to
 * the cent, which is what formatMoney/formatMoneyWhole are for.
 */
export function formatMoneyCompact(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${Math.round(abs / 1_000)}k`;
  }
  return formatMoneyWhole(amount);
}

/** Cents-precise display for entry rows ("$41,300.00"). */
export function formatMoney(amount: number): string {
  return moneyFormatterCents.format(amount);
}

/**
 * Two decimal places, always — "7.50", never "7.5" and never `$`. Hours.tsx and
 * TimeClock.tsx each already render hours this exact way (`(n).toFixed(2)`); this is the
 * shared home for that shape rather than a third private copy, for PayRuns.tsx (ef-034).
 */
export function formatHours(hours: number): string {
  return hours.toFixed(2);
}

/**
 * The +/- shown next to an amount, per account kind.
 *
 * ⚠️ MIRRORED FROM C#, NOT DECIDED HERE. The rule lives in
 * `src/EasyFarm.Domain/PostingRules.cs` (`PostingRules.DisplaySign`) — one
 * netstandard2.0 implementation that runs inside the API and inside the offline
 * client, which is the whole point of ADR 0001. A browser cannot call into that
 * assembly, so this table has to exist; what does NOT have to exist is the
 * ability for it to drift. `AccountKindParityTests` (tests/EasyFarm.Domain.Tests)
 * parses THIS literal out of THIS file and fails `dotnet test` if any row
 * disagrees with the C# rule, so editing a sign here without editing
 * PostingRules.cs turns the .NET suite red rather than quietly inverting a
 * farmer's P&L. Before ef-001 there was no C# rule to disagree with: this
 * function was the only sign rule in the codebase.
 *
 * Note the PascalCase keys: AccountResponse serializes AccountKind via a bare
 * JsonStringEnumConverter (no naming policy), which emits the C# member name
 * as-is — see the header comment in api/types.ts.
 */
export const DISPLAY_SIGN_BY_ACCOUNT_KIND: Record<AccountKind, "+" | "-"> = {
  Income: "+",
  Expense: "-",
  Asset: "-",
  Liability: "-",
  Equity: "-",
};

/**
 * Entries are stored as a positive magnitude; the +/- shown to the user is
 * derived purely from the account kind. This is a display rule, not arithmetic
 * — see api/types.ts CreateEntryRequest.
 *
 * An entry with no account kind at all (the concurrent-visitor e2e fixture posts
 * one, and the Capture screen can save before a category is picked) has nothing
 * to classify it, and renders as a debit — the same default EntryRow.tsx has
 * always shown. That default is deliberately NOT in the table above: the table
 * is the parity contract with the C# enum, which has no "unknown" member.
 */
export function signForAccountKind(kind: AccountKind | null | undefined): "+" | "-" {
  if (kind == null) return "-";
  return DISPLAY_SIGN_BY_ACCOUNT_KIND[kind] ?? "-";
}

export function formatSignedMoney(amount: number, kind: AccountKind | null | undefined): string {
  return `${signForAccountKind(kind)}${formatMoney(Math.abs(amount))}`;
}

/**
 * Signed whole-dollar text for a live KPI delta ("-$413", "+$1,875") — the
 * flash badge on the dashboard's live KPI cards (KpiCard `flashDelta`,
 * lib/kpiDerivation.ts `deriveLiveDelta`).
 *
 * Deliberately NOT `formatSignedMoney`: that one derives its sign from an
 * account-KIND lookup and prints cents, matching a single entry row. This one
 * takes an already-signed dollar amount (a delta can be the sum of several
 * entries) and rounds to whole dollars, matching the convention every other
 * figure on this screen uses (`formatMoneyWhole`) — a delta chip showing
 * cents next to KPI figures that never do would read as a different kind of
 * number.
 */
export function formatSignedDelta(amountDollars: number): string {
  const sign = amountDollars < 0 ? "-" : "+";
  return `${sign}${formatMoneyWhole(Math.abs(amountDollars))}`;
}

const relativeFormatter = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });

/** "just now" / "2m ago" style label for the live-updating entries list. */
export function formatRelativeTime(isoTimestamp: string, now: Date = new Date()): string {
  const then = new Date(isoTimestamp).getTime();
  const diffSeconds = Math.round((then - now.getTime()) / 1000);

  const thresholds: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, "second"],
    [3600, "minute"],
    [86400, "hour"],
  ];

  for (const [limit, unit] of thresholds) {
    if (Math.abs(diffSeconds) < limit) {
      const divisor = unit === "second" ? 1 : unit === "minute" ? 60 : 3600;
      return relativeFormatter.format(Math.round(diffSeconds / divisor), unit);
    }
  }
  return relativeFormatter.format(Math.round(diffSeconds / 86400), "day");
}

export function formatDateShort(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * ef-018 — how much later the server actually received an offline-recorded punch, said the
 * way a person would say it: "received 35 minutes later", not "received 0.58 hours later"
 * and never the raw ISO stamp. `referenceIso` is whichever of the shift's own timestamps
 * this delay is measured against — `endedAt` once the shift is closed, `startedAt` for one
 * still running (`recordedOffline`/`receivedAt` on `TimeEntry`, api/types.ts).
 *
 * @ui-check: a near-zero gap reads "received right away", never "0 minutes later" — the
 * number would be technically true but would read like the client mishandled an empty
 * offline queue rather than like a phone that reconnected almost immediately.
 */
export function formatOfflineDelay(receivedAt: string, referenceIso: string): string {
  const minutes = (new Date(receivedAt).getTime() - new Date(referenceIso).getTime()) / 60_000;

  if (minutes < 1) return "received right away";

  if (minutes < 60) {
    const m = Math.max(1, Math.round(minutes));
    return `received ${m} minute${m === 1 ? "" : "s"} later`;
  }

  if (minutes < 60 * 24) {
    const h = Math.round((minutes / 60) * 10) / 10;
    return `received ${h} hour${h === 1 ? "" : "s"} later`;
  }

  const d = Math.round((minutes / (60 * 24)) * 10) / 10;
  return `received ${d} day${d === 1 ? "" : "s"} later`;
}
